import { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";

/**
 * Subscribes to the current user's own users/{uid} doc and returns their
 * capabilities map plus their notification preferences. A user can always
 * read their own doc, so this works for every role. Owner has no user doc
 * → empty maps (owner is gated by isOwner everywhere anyway).
 */
export default function useCapabilities(user) {
  const [capabilities, setCapabilities] = useState({});
  const [department, setDepartment] = useState("");
  const [notificationPreferences, setNotificationPreferences] = useState({});
  const [profile, setProfile] = useState({});
  // Per-user "I've already cleared these" list. Notifications are org-wide;
  // dismissing them on the client just hides them from THIS user's bell —
  // other org members still see them.
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState([]);
  // Per-thread "last time I opened this thread" — used by MessagesPage and
  // the dashboard counter to compute unread state on a per-user basis.
  const [threadLastRead, setThreadLastRead] = useState({});

  useEffect(() => {
    if (!user) {
      setCapabilities({});
      setDepartment("");
      setNotificationPreferences({});
      setProfile({});
      setDismissedNotificationIds([]);
      setThreadLastRead({});
      return;
    }
    const unsub = onSnapshot(
      doc(db, `users/${user.uid}`),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setCapabilities(data.capabilities || {});
        setDepartment(data.department || "");
        setNotificationPreferences(data.notificationPreferences || {});
        setProfile(data.profile || {});
        setDismissedNotificationIds(data.dismissedNotificationIds || []);
        setThreadLastRead(data.threadLastRead || {});
      },
      (err) => {
        console.warn("capabilities read failed:", err.message);
        setCapabilities({});
        setDepartment("");
        setNotificationPreferences({});
        setProfile({});
        setDismissedNotificationIds([]);
        setThreadLastRead({});
      }
    );
    return unsub;
  }, [user]);

  return {
    capabilities,
    department,
    notificationPreferences,
    profile,
    dismissedNotificationIds,
    threadLastRead,
  };
}
